/**
 * CountryContext — Gestion globale du pays (France / Suisse) pour CONFILEX.
 *
 * Stratégie :
 *  - Persistance dans localStorage (clé `confilex_country`)
 *  - Détection automatique au premier chargement via navigator.language
 *    (fr-CH / de-CH / it-CH → CH ; sinon FR par défaut)
 *  - Override manuel via setCountry(code)
 *  - Émet un événement window 'confilex:country:changed' pour les listeners
 */
import { createContext, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "confilex_country";

export const COUNTRIES = {
  FR: {
    code: "FR",
    label: "CONFILEX FRANCE",
    short: "France",
    flag: "🇫🇷",
    city: null,
    domain: "confilex.fr",
    locale: "fr-FR",
    currency: "EUR",
    description:
      "Avocats, experts-comptables, notaires & CGP référencés sur tout le territoire français. Cadre normatif français, EUR.",
    available: true,
  },
  CH: {
    code: "CH",
    label: "CONFILEX.CH · GENÈVE",
    short: "Suisse",
    flag: "🇨🇭",
    city: "Genève",
    domain: "confilex.ch",
    locale: "fr-CH",
    currency: "CHF",
    description:
      "Avocats, fiduciaires & notaires référencés à Genève et en Suisse romande. Cadre normatif suisse, CHF.",
    available: true,
  },
};

function detectCountry() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && COUNTRIES[stored]) return stored;
  } catch { /* localStorage indisponible */ }
  try {
    const langs = navigator.languages || [navigator.language || ""];
    for (const l of langs) {
      const low = (l || "").toLowerCase();
      if (low.includes("-ch") || low === "fr-ch" || low === "de-ch" || low === "it-ch") {
        return "CH";
      }
    }
    // Si l'hôte est confilex.ch, on présume CH
    if (typeof window !== "undefined" && window.location?.hostname?.endsWith(".ch")) {
      return "CH";
    }
  } catch { /* navigator indisponible */ }
  return "FR";
}

const CountryContext = createContext({
  country: "FR",
  countryData: COUNTRIES.FR,
  setCountry: () => {},
  isResolved: false,
});

export function CountryProvider({ children }) {
  const [country, setCountryState] = useState("FR");
  const [isResolved, setIsResolved] = useState(false);

  useEffect(() => {
    const c = detectCountry();
    setCountryState(c);
    setIsResolved(true);
  }, []);

  const setCountry = (code) => {
    if (!COUNTRIES[code]) return;
    setCountryState(code);
    try { localStorage.setItem(STORAGE_KEY, code); } catch { /* ignore */ }
    try { window.dispatchEvent(new CustomEvent("confilex:country:changed", { detail: code })); } catch { /* ignore */ }
  };

  const value = useMemo(() => ({
    country,
    countryData: COUNTRIES[country] || COUNTRIES.FR,
    setCountry,
    isResolved,
  }), [country, isResolved]);

  return <CountryContext.Provider value={value}>{children}</CountryContext.Provider>;
}

export function useCountry() {
  return useContext(CountryContext);
}
